import { type FormikProps } from "formik";
import { Component } from "react";

import { ValidatedForm } from "../../../../../../components/forms";
import { type EmailFormProps, type EmailFormState } from "../types";
import { type EmailView } from "../../types/state";
import ExpandableLinks from "../../../../../../components/ExpandableItems/ExpandableLinks/ExpandableLinks";
import { RolePermissions } from "../../../../../../enums";
import { connect, type ConnectedProps } from "react-redux";
import { type RootState } from "features/Application/globaltypes/redux";

export type EmailFormPropsAll = EmailFormProps & FormikProps<EmailView> & PropsFromRedux;
const utmCampaignField = "utmCampaign";

export class EmailForm extends Component<EmailFormPropsAll, EmailFormState> {
  onTitleBlur = (propertyName: string) => {
    const { values, setFieldValue, onBlur, touched } = this.props;
    if (values.id <= 0 && !touched[utmCampaignField]) {
      setFieldValue(utmCampaignField, values.title, true);
    }

    onBlur(propertyName);
  };

  render() {
    const { dirty, isLoading, disablePreventTransitionPrompt } = this.props;

    return (
      <ValidatedForm
        dirty={dirty}
        unsavedChangesPrompt={{
          title: "Exit Without Saving?",
          message: "Are you sure you want to exit without saving this email? All information entered will be lost.",
        }}
        isLoading={isLoading}
        isSubmitting={isLoading}
        formWidthComputer={6}
        withoutFormik
        disablePreventTransitionPrompt={disablePreventTransitionPrompt}
      >
        {this.renderFields}
      </ValidatedForm>
    );
  }

  renderFields = (): any => {
    const { tags, values, disabled, save, onBlur, onIsValidChange, userPermissions, isReadOnlyView } = this.props;
    const validatedFieldProps = this.getValidateFieldProps();

    return (
      <div className="email-form-container">
        <ValidatedForm.InputField
          label="Title"
          placeholder="Enter title"
          value={values.title}
          propertyName="title"
          markAsRequired
          onBlur={this.onTitleBlur}
          disabled={disabled}
          isReadOnlyView={isReadOnlyView}
          {...validatedFieldProps}
        />
        <ValidatedForm.InputField
          label="Subject Line"
          placeholder="Enter subject"
          propertyName="subject"
          markAsRequired
          value={values.subject}
          onBlur={onBlur}
          onIsFormValidChange={onIsValidChange}
          disabled={disabled}
          isReadOnlyView={isReadOnlyView}
          {...validatedFieldProps}
        />
        <ValidatedForm.InputField
          label="Preview Text"
          value={values.previewText}
          placeholder="Enter preview text"
          propertyName="previewText"
          onBlur={onBlur}
          disabled={disabled}
          isReadOnlyView={isReadOnlyView}
          readOnlyEmptyState="No preview text"
          {...validatedFieldProps}
        />
        <ValidatedForm.MultiSelectWithAdditionField
          label="Software Application"
          propertyName="softwareApplications"
          className="software-applications"
          value={values.softwareApplications.selected}
          options={tags?.softwareApplications}
          onBlur={onBlur}
          onChanged={save}
          disabled={disabled}
          isReadOnlyView={isReadOnlyView}
          readOnlyEmptyState="No software applications selected"
          {...validatedFieldProps}
        />
        <ValidatedForm.MultiSelectWithAdditionField
          label="Labels"
          propertyName="labels"
          value={values.labels.selected}
          options={tags?.labels}
          onBlur={onBlur}
          onChanged={save}
          disabled={disabled}
          isReadOnlyView={isReadOnlyView}
          readOnlyEmptyState="No labels selected"
          {...validatedFieldProps}
        />
        <ExpandableLinks
          label="Associated Flows"
          linkFormatter={(linkId) => `/content/flows/${linkId}/items`}
          iconName="fa-code-branch"
          links={values.flows}
          isReadOnlyView={isReadOnlyView}
          readOnlyEmptyState={"No associated Flows"}
        />
        {userPermissions.includes(RolePermissions.PacksManage) &&
          !values.isPurchased &&
          !values.isDuplicatedFromPurchased && (
            <ValidatedForm.CheckboxField
              toggle
              label={"Allow customers to duplicate this email?"}
              value={values.isPurchasedDuplicationAllowed}
              propertyName={"isPurchasedDuplicationAllowed"}
              disabled={disabled}
              onChanged={save}
              {...validatedFieldProps}
            />
          )}
        <ValidatedForm.InputField
          label="Campaign Name"
          placeholder="Campaign Name"
          value={values.utmCampaign}
          propertyName="utmCampaign"
          markAsRequired
          onBlur={onBlur}
          disabled={disabled}
          tooltip={{
            info: "Sets the campaign name for tracking in analytics. Defaults to your email title but can be customized.",
            width: 20,
          }}
          isReadOnlyView={isReadOnlyView}
          {...validatedFieldProps}
        />
      </div>
    );
  };

  getValidateFieldProps = (): any => {
    const { errors, touched, isValid, handleChange, handleBlur, setFieldValue, setFieldTouched, dirty, resetForm } =
      this.props;

    return {
      errors,
      touched,
      isFormValid: isValid,
      handleChange,
      handleBlur,
      setFieldValue,
      setFieldTouched,
      dirty,
      resetForm,
    };
  };
}

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({
  userPermissions: state.userProfile.permissions,
});

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EmailForm);
export default ConnectedComponent;
